import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as GarageIcon } from 'assets/icons/ico_garage_black.svg';
import { ReactComponent as ElectricityIcon } from 'assets/icons/ico_electricity_black.svg';
import { ReactComponent as FenceIcon } from 'assets/icons/ico_fence_black.svg';
import { ReactComponent as SunIcon } from 'assets/icons/ico_sun.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/ico_close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/ico_info_circle_blue.svg';
import { ReactComponent as HeaterChargingIconBlack } from 'assets/icons/ico_engine_heat_black.svg';
import { ReactComponent as DisabilityParkingIconBlack } from 'assets/icons/ico_disability_parking_black.svg';
import { ReactComponent as CaravanIconBlack } from 'assets/icons/ico_camper_black.svg';
import { ReactComponent as MobileHomeIconBlack } from 'assets/icons/ico_garage_filter_black.svg';

import Paging from 'components/common/paging';
import ButtonText from 'components/common/buttonText';
import CheckBox from 'components/common/checkbox/checkbox';
import ToggleText from 'components/common/toggleText';
import InputSearch from 'components/common/inputSearch';

const SelectParkingSpotsModal = ({ display, setDisplay, selectedParkingSpots, setSelectedParkingSpots, parkingSpots, isSingleSelectionOnly, showPrice = false, children }) => {

    const { t } = useTranslation();
    const handleOverlayOnClick = (e) => {
        closeModal();
        e.stopPropagation();
    }

    const closeModal = () => {
        document.body.classList.remove('overflow-hidden');
        setDisplay(false);
    }

    const itemsPerPage = 8;
    const [localParkingSpots, setLocalParkingSpots] = useState(parkingSpots);
    const [filteredLocalParkingSpots, setFilteredLocalParkingSpots] = useState(parkingSpots);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [parkingSpotPages, setParkingSpotPages] = useState();
    const [currentParkingSpotPage, setCurrentParkingSpotPage] = useState(0);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        if (selectedParkingSpots && parkingSpots) {
            const parkingSpotsWithCheckedStatus = parkingSpots.map((parkingSpot) => {
                const parkingSpotResult = selectedParkingSpots.find(({ parkingSpotExtId }) => parkingSpotExtId === parkingSpot.parkingSpotExtId);

                return {
                    ...parkingSpot,
                    isChecked: !!parkingSpotResult
                };
            });

            setLocalParkingSpots(parkingSpotsWithCheckedStatus);
        }
    }, [selectedParkingSpots, parkingSpots]);

    useEffect(() => {
        if (filteredLocalParkingSpots.length > 0) {
            const allFilteredSelected = filteredLocalParkingSpots.every(spot => 
                selectedParkingSpots.some(selected => selected.parkingSpotExtId === spot.parkingSpotExtId)
            );
            setIsAllChecked(allFilteredSelected);
        } else {
            setIsAllChecked(false);
        }
    }, [selectedParkingSpots, filteredLocalParkingSpots]);

    useEffect(() => {
        if (filteredLocalParkingSpots) {
            setParkingSpotPages(Math.ceil(filteredLocalParkingSpots.length / itemsPerPage));
        }
    }, [filteredLocalParkingSpots]);

    useEffect(() => {
        if (display) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }, [display]);

    useEffect(()=> {
        let result = showSelectedOnly
            ? localParkingSpots.filter((parkingSpot) => parkingSpot.isChecked)
            : localParkingSpots;

        const q = (searchText || '').trim().toLowerCase();
        if (q) {
            result = result.filter((ps) => {
                const id = (ps.parkingSpotExtId || '').toLowerCase();
                const obj = (ps.objectID || '').toLowerCase();
                const spot = (ps.spotIdentifier || '').toString().toLowerCase();
                const floor = (ps.floor ?? '').toString().toLowerCase();
                return id.includes(q) || obj.includes(q) || spot.includes(q) || floor.includes(q);
            });
        }
        setFilteredLocalParkingSpots(result);
        setCurrentParkingSpotPage(0);
    }, [showSelectedOnly, localParkingSpots, searchText]);

    const handleParkingSpotItemSelect = (parkingSpot) => {
        // Find the original parking spot from the parkingSpots prop
        const originalSpot = parkingSpots.find(
            spot => spot.parkingSpotExtId === parkingSpot.parkingSpotExtId
        );

        if (parkingSpot.isChecked) {
            /* It was checked previously, remove */
            const updatedSelection = selectedParkingSpots.filter(
                ({ parkingSpotExtId }) => parkingSpotExtId !== parkingSpot.parkingSpotExtId
            );
            setSelectedParkingSpots(updatedSelection);
        } else {
            /* Else add */
            const result = selectedParkingSpots.find(({ parkingSpotExtId }) => parkingSpotExtId === parkingSpot.parkingSpotExtId);

            if (!result && originalSpot) {
                if (!isSingleSelectionOnly) {
                    setSelectedParkingSpots([...selectedParkingSpots, originalSpot]);
                } else {
                    setSelectedParkingSpots([originalSpot]);
                }
            }
        }
    }

    const handleSelectAllParkingSpots = () => {
        if (!isSingleSelectionOnly) {
            // Check if all filtered spots are currently selected
            const allFilteredSelected = filteredLocalParkingSpots.every(spot => 
                selectedParkingSpots.some(selected => selected.parkingSpotExtId === spot.parkingSpotExtId)
            );

            if (allFilteredSelected) {
                // Deselect all filtered spots
                const filteredIds = new Set(filteredLocalParkingSpots.map(spot => spot.parkingSpotExtId));
                const remainingSelected = selectedParkingSpots.filter(spot => !filteredIds.has(spot.parkingSpotExtId));
                setSelectedParkingSpots(remainingSelected);
            } else {
                // Select all filtered spots - use original parkingSpots, not localParkingSpots
                const selectedIds = new Set(selectedParkingSpots.map(spot => spot.parkingSpotExtId));
                const filteredIds = new Set(filteredLocalParkingSpots.map(spot => spot.parkingSpotExtId));
                const newSelections = parkingSpots.filter(spot => 
                    filteredIds.has(spot.parkingSpotExtId) && !selectedIds.has(spot.parkingSpotExtId)
                );
                setSelectedParkingSpots([...selectedParkingSpots, ...newSelections]);
            }
        }
    }

    if (!display) {
        return (null);
    }

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 z-50 bg-black bg-opacity-60">
            </div>
            {/* Modal */}
            <div onClick={(e) => handleOverlayOnClick(e)} className="fixed inset-0 z-50 overflow-y-scroll overflow-x-hidden px-6">
                <div onClick={(e) => e.stopPropagation()} className="flex mr-auto ml-auto mt-6 mb-6 flex-col max-w-[1140px] p-6 w-full justify-center rounded-xl overflow-y-hidden items-center bg-white">
                    <div className="flex w-full justify-end mb-6">
                        <button className="h-4" onClick={() => { closeModal(); }}>
                            <CloseIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="flex flex-col rounded-xl shadow p-6 w-full">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                            <h2 className="text-xl font-medium">{isSingleSelectionOnly ? t('selectParkingSpotsModal.selectParkingSpot') : t('selectParkingSpotsModal.selectParkingSpots')}</h2>

                            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:flex-1 sm:max-w-[520px]">
                                <div className="flex-1">
                                    <InputSearch
                                        value={searchText}
                                        onChange={setSearchText}
                                        placeHolder={t('selectParkingSpotsModal.searchPlaceholder')}
                                    />
                                </div>
                                <div className="w-[180px] shrink-0">
                                    <ToggleText
                                        defaultText={t('All')}
                                        toggledText={t('Selected')}
                                        isToggled={showSelectedOnly} onClick={() => {
                                            setShowSelectedOnly(!showSelectedOnly);
                                        }}/>
                                </div>
                            </div>
                        </div>
                        <div className="-mr-6 -ml-6 airpark-scroll overflow-x-scroll">
                            <table className="table-auto w-full">
                                <thead>
                                    <tr className="text-left h-11 bg-airpark-gray-300">
                                        <th className="pl-6">
                                            <CheckBox isChecked={isAllChecked} onClick={() => {
                                                handleSelectAllParkingSpots();
                                            }} />
                                        </th>
                                        <th className="pl-6">{t('selectParkingSpotsModal.id')}</th>
                                        <th className="pl-6">{t('selectParkingSpotsModal.objectId')}</th>
                                        <th className="pl-6">{t('selectParkingSpotsModal.locationSpecific')}</th>
                                        <th className="pl-6">{t('selectParkingSpotsModal.floor')}</th>
                                        <th className="pl-6">{t('selectParkingSpotsModal.number')}</th>
                                        <th className="pl-6 pr-6">{t('selectParkingSpotsModal.features')}</th>
                                        {showPrice && <th className="pl-6 pr-6">{t('selectParkingSpotsModal.price')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLocalParkingSpots && filteredLocalParkingSpots.length > 0 && filteredLocalParkingSpots.slice(currentParkingSpotPage * itemsPerPage, ((currentParkingSpotPage * itemsPerPage) + itemsPerPage)).map((ps, i) => {
                                        return (
                                            <tr key={i} className="h-16 border-b-[1px] last:border-b-[0px]">
                                                <td className="pl-6">
                                                    <CheckBox isChecked={ps.isChecked ?? false} onClick={() => {                                                        
                                                        if(!ps.isDisabled){
                                                            handleParkingSpotItemSelect(ps);
                                                        }
                                                    }} />
                                                </td>
                                                <td className="pl-6">{ps.parkingSpotExtId}</td>
                                                <td className="pl-6">{ps.objectID || '—'}</td>
                                                {ps.isNumbered &&
                                                    <td className="pl-6">{t('selectParkingSpotsModal.yes')}</td>
                                                }
                                                {!ps.isNumbered &&
                                                    <td className="pl-6">{t('selectParkingSpotsModal.no')}</td>
                                                }
                                                <td className="pl-6">{ps.floor ?? t('parkingArea.unspecified')}</td>
                                                {ps.isNumbered &&
                                                    <td className="pl-6">{ps.spotIdentifier}</td>
                                                }
                                                {!ps.isNumbered &&
                                                    <td className="pl-6">{t('selectParkingSpotsModal.notNumbered')}</td>
                                                }
                                                <td className="pl-6 pr-6">
                                                    <div className="flex">
                                                        {ps.isIndoor &&
                                                            <GarageIcon className="h-6 w-6" />
                                                        }
                                                        {!ps.isIndoor &&
                                                            <SunIcon className="h-6 w-6" />
                                                        }
                                                        {ps.hasChargingPost &&
                                                            <ElectricityIcon className="h-6 w-6 ml-2" />
                                                        }
                                                        {ps.isFenced &&
                                                            <FenceIcon className="h-6 w-6 ml-2" />
                                                        }
                                                        {ps.isHeaterCharging &&
                                                            <HeaterChargingIconBlack className="h-6 w-6 ml-2" />
                                                        }
                                                        {ps.isDisabilityParking &&
                                                            <DisabilityParkingIconBlack className="h-6 w-6 ml-2" />
                                                        }
                                                        {ps.isCaravan &&
                                                            <CaravanIconBlack className="h-6 w-6 ml-2" />
                                                        }
                                                        {ps.isMobileHome &&
                                                            <MobileHomeIconBlack className="h-6 w-6 ml-2" />
                                                        }
                                                    </div>
                                                </td>
                                                {showPrice && <td className="pl-6 pr-6">{ps.price ?? t('parkingArea.unspecified')}</td>}
                                            </tr>
                                        )
                                    })
                                    }
                                    {parkingSpots && parkingSpots.length < 1 &&
                                        <tr className="h-16">
                                            <td className="text-center" colSpan={showPrice ? 8 : 7}>
                                                <div className="flex pl-6 gap-3 flex-row items-center justify-left">
                                                    <InfoIcon className="h-6 w-6" />
                                                    <span>{t('selectParkingSpotsModal.noParkingSpotsAvailable')}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                    {parkingSpots && parkingSpots.length > 0 && filteredLocalParkingSpots.length < 1 &&
                                        <tr className="h-16">
                                            <td className="text-center" colSpan={showPrice ? 8 : 7}>
                                                <div className="flex pl-6 gap-3 flex-row items-center justify-left text-gray-500">
                                                    <InfoIcon className="h-6 w-6" />
                                                    <span>{t('selectParkingSpotsModal.noSearchResults')}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                        <Paging totalNumberOfPages={parkingSpotPages} currentPage={currentParkingSpotPage} setCurrentPage={setCurrentParkingSpotPage} />
                    </div>
                    

                    <div className="flex flex-col w-full items-start justify-between mt-3">
                        <div className="flex">
                            {t('selectParkingSpotsModal.numberOfParkingSpotsSelected', { number: selectedParkingSpots.length })}
                        </div>
                        {children}
                        
                    </div>
                    <div className="flex  w-full justify-end flex-1 mt-3">
                    <ButtonText onClick={() => { closeModal(); }} className="w-80">
                            {t('generic.ok')}
                        </ButtonText>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SelectParkingSpotsModal;